import { useEffect, useState, useRef, useCallback } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowLeft, Check, Copy, Send, ChevronDown, ChevronUp, Search, FileText, Mic, Link, ThumbsUp, ThumbsDown, Printer, Download, X, Brain, CheckSquare, Square } from 'lucide-react'
import { diffWords, type DiffPart } from '../lib/wordDiff'
import { api } from '../lib/api'
import { micErrorMessage } from '../lib/micError'
import { LetterGenerator } from '../components/note/LetterGenerator'

// Textarea that grows to fit its content (no inner scrollbar). Used for note section
// editing in both Sections and Full view so long wrapped paragraphs (HPI/Assessment)
// don't collapse into a tiny scroll window on large screens.
function AutoGrowTextarea({ value, onChange, className }: { value: string; onChange: (v: string) => void; className?: string }) {
  const ref = useRef<HTMLTextAreaElement>(null)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }, [value])
  return (
    <textarea
      ref={ref}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      rows={1}
      className={className}
      style={{ overflow: 'hidden', resize: 'none' }}
    />
  )
}

function PatientContextPanel({ patientId, currentEncounterId }: { patientId: string; currentEncounterId: string }) {
  const [patient, setPatient] = useState<any>(null)
  const [encounters, setEncounters] = useState<any[]>([])
  const [intakes, setIntakes] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedIntake, setSelectedIntake] = useState<string | null>(() => {
    try { return localStorage.getItem(`scribey_ctx_intake_${patientId}`) || null } catch { return null }
  })
  function selectIntake(intakeId: string) {
    setSelectedIntake(prev => {
      const next = prev === intakeId ? null : intakeId
      try { if (next) localStorage.setItem(`scribey_ctx_intake_${patientId}`, next); else localStorage.removeItem(`scribey_ctx_intake_${patientId}`) } catch {}
      return next
    })
  }

  useEffect(() => {
    async function load() {
      try {
        const [p, encs, intk] = await Promise.all([
          api.getPatient(patientId),
          api.getPatientEncounters(patientId),
          api.getPatientIntakeSessions(patientId).catch(() => [] as any[]),
        ])
        setPatient(p)
        // Filter out current encounter and sort by date desc
        setEncounters(encs.filter((e: any) => e.id !== currentEncounterId).slice(0, 5))
        setIntakes(intk || [])
        try {
          const stored = localStorage.getItem(`scribey_ctx_intake_${patientId}`)
          if (!stored && intk && intk.length > 0) {
            // Default: attach the most recent intake so partial intakes still inform the note.
            localStorage.setItem(`scribey_ctx_intake_${patientId}`, intk[0].id)
            setSelectedIntake(intk[0].id)
          }
        } catch {}
      } catch (err) {
        console.error('Failed to load patient context:', err)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [patientId, currentEncounterId])

  if (loading) return <div className="text-sm text-gray-400 dark:text-gray-500">Loading patient context...</div>
  if (!patient) return <div className="text-sm text-gray-400 dark:text-gray-500">Patient not found.</div>

  return (
    <div className="space-y-3">
      <div>
        <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{patient.last_name}, {patient.first_name}</p>
        {patient.dob && <p className="text-xs text-gray-400 dark:text-gray-500">DOB: {patient.dob}</p>}
        {/* AI documentation consent status */}
        {patient.ai_consent === true && (
          <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">✓ AI documentation consent on file{patient.ai_consent_source ? ` (${patient.ai_consent_source})` : ''}</p>
        )}
        {patient.ai_consent === false && (
          <p className="text-xs text-red-600 dark:text-red-400 mt-1">✕ Patient declined AI documentation</p>
        )}
      </div>

      {/* Pre-visit intake forms — toggle into note context (default on for most recent) */}
      {intakes.length > 0 && (
        <div>
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2 flex items-center gap-1">
            <Brain size={11} /> Pre-visit Intake ({selectedIntake ? 1 : 0}/{intakes.length})
          </p>
          <div className="space-y-1">
            {intakes.slice(0, 3).map((s: any) => {
              const on = selectedIntake === s.id
              return (
                <button key={s.id} type="button" onClick={() => selectIntake(s.id)}
                  title={on ? 'Included in note context — click to exclude' : 'Click to include this intake in the note context'}
                  className={`w-full flex items-start gap-2 px-2 py-1.5 rounded-lg text-left transition-colors ${on ? 'hover:bg-gray-100 dark:hover:bg-gray-800' : 'opacity-40 hover:opacity-60'}`}>
                  <span className="flex-shrink-0 mt-0.5">
                    {on ? <CheckSquare size={13} className="text-emerald-500" /> : <Square size={13} className="text-gray-400" />}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex justify-between items-center gap-2">
                      <p className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate">
                        {s.visit_reason || 'Intake'} — {new Date(s.created_at).toLocaleDateString()}
                      </p>
                      <span className={`flex-shrink-0 text-xs px-1.5 py-0.5 rounded ${s.status === 'completed' ? 'bg-emerald-600/20 text-emerald-400' : s.status === 'in_progress' ? 'bg-amber-600/20 text-amber-500' : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'}`}>
                        {`${Math.round(s.completion_pct || 0)}%`}
                      </span>
                    </div>
                    {s.summary && <p className="text-xs text-gray-400 dark:text-gray-500 line-clamp-2 mt-0.5">{s.summary}</p>}
                  </div>
                </button>
              )
            })}
          </div>
        </div>
      )}

      {encounters.length > 0 ? (
        <div>
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Prior Visits ({encounters.length})</p>
          <div className="space-y-2">
            {encounters.map((enc: any) => (
              <div key={enc.id} className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                    {enc.encounter_type || 'Visit'} — {new Date(enc.encounter_date || enc.created_at).toLocaleDateString()}
                  </span>
                  <span className={`text-xs px-1.5 py-0.5 rounded ${enc.status === 'draft' ? 'bg-sky-600/20 text-sky-400' : enc.status === 'final' ? 'bg-emerald-600/20 text-emerald-400' : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'}`}>
                    {enc.status}
                  </span>
                </div>
                {enc.note_text ? (
                  <pre className="text-xs text-gray-500 dark:text-gray-400 whitespace-pre-wrap font-sans line-clamp-6">{enc.note_text.slice(0, 600)}{enc.note_text.length > 600 ? '...' : ''}</pre>
                ) : (
                  <p className="text-xs text-gray-400 dark:text-gray-500 italic">No note generated yet</p>
                )}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <p className="text-xs text-gray-400 dark:text-gray-500">{intakes.length > 0 ? 'No prior visits — see pre-visit intake above.' : 'No prior visits found for this patient.'}</p>
      )}
    </div>
  )
}

interface CriticFlag {
  type?: string
  severity?: string
  section?: string
  issue?: string
  detail?: string
  suggestion?: string
}

interface Note {
  id: string
  encounter_id: string
  sections: Record<string, string>
  full_text: string
  status: string
  critic_score: number | null
  critic_flags: CriticFlag[]
  missing_sections: Array<{ section: string; label: string; severity: string }>
  suggested_icd10: Record<string, { desc: string; confidence: number }>
  suggested_cpt: Record<string, { desc: string; confidence: number }>
  suggested_em_level: string | null
  version: number
  created_at: string
}

interface Encounter {
  id: string
  patient_id: string | null
  patient_name: string | null
  encounter_type: string
  encounter_date: string
  transcript: string
  status: string
}

type ViewMode = 'sections' | 'full'
type TabType = 'context' | 'note' | 'codes' | 'instructions' | 'letters'

const CONFETTI_PIECES = ['🎉', '✨', '🎊', '⭐', '💫', '🌟']

// Decode a recorded audio Blob (webm/opus from MediaRecorder) into raw 16-bit LE,
// 16 kHz, mono PCM — the format AWS Transcribe (and Deepgram) accept via
// transcribe_prerecorded. Doing this client-side means the PTT command audio goes
// to the SAME configured provider as live dictation (no hardcoded Deepgram).
async function blobToPcm16kMono(blob: Blob): Promise<Blob> {
  const arrayBuf = await blob.arrayBuffer()
  const AC: typeof AudioContext = (window.AudioContext || (window as any).webkitAudioContext)
  const decodeCtx = new AC()
  let decoded: AudioBuffer
  try {
    decoded = await decodeCtx.decodeAudioData(arrayBuf.slice(0))
  } finally {
    decodeCtx.close()
  }
  const targetRate = 16000
  const durationSec = decoded.length / decoded.sampleRate
  const frames = Math.max(1, Math.ceil(durationSec * targetRate))
  const offline = new OfflineAudioContext(1, frames, targetRate)
  const srcNode = offline.createBufferSource()
  srcNode.buffer = decoded
  srcNode.connect(offline.destination)
  srcNode.start(0)
  const rendered = await offline.startRendering()
  const ch = rendered.getChannelData(0)
  // Float32 [-1,1] -> Int16 LE
  const pcm = new Int16Array(ch.length)
  for (let i = 0; i < ch.length; i++) {
    let s = Math.max(-1, Math.min(1, ch[i]))
    pcm[i] = s < 0 ? s * 0x8000 : s * 0x7fff
  }
  return new Blob([pcm.buffer], { type: 'application/octet-stream' })
}


export function NotePage() {
  const { id, noteId } = useParams<{ id: string; noteId?: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [showConfetti, setShowConfetti] = useState(false)

  // Show confetti when arriving from recording
  useEffect(() => {
    if (searchParams.get('from') === 'recording') {
      setShowConfetti(true)
      const timer = setTimeout(() => setShowConfetti(false), 2500)
      return () => clearTimeout(timer)
    }
  }, [])

  const [encounter, setEncounter] = useState<Encounter | null>(null)
  const [note, setNote] = useState<Note | null>(null)
  // Always-current mirror of `note` so async callbacks (e.g. PTT recorder.onstop)
  // diff against the displayed note, never a stale closure capture.
  const noteRef = useRef<Note | null>(null)
  useEffect(() => { noteRef.current = note }, [note])
  const [loading, setLoading] = useState(true)
  const [generating, setGenerating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [editedSections, setEditedSections] = useState<Record<string, string>>({})
  const [originalSections, setOriginalSections] = useState<Record<string, string>>({})
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set())

  const [activeTab, setActiveTab] = useState<TabType>('note')
  const [viewMode, setViewMode] = useState<ViewMode>(window.innerWidth < 768 ? 'full' : 'sections')
  const [copiedSection, setCopiedSection] = useState<string | null>(null)

  const [revisionMessage, setRevisionMessage] = useState('')
  const [isRevising, setIsRevising] = useState(false)
  const [updatedSections, setUpdatedSections] = useState<Set<string>>(new Set())
  // Word-level diffs of the last quick edit (typed or voice), per section. Cleared per-section on click.
  const [revisionDiffs, setRevisionDiffs] = useState<Record<string, DiffPart[]>>({})

  const [transcriptSearch, setTranscriptSearch] = useState('')

  // PTT (hold-to-talk) voice edit state
  const [isPttRecording, setIsPttRecording] = useState(false)
  const [isPttProcessing, setIsPttProcessing] = useState(false)
  const pttRecorderRef = useRef<MediaRecorder | null>(null)
  const pttChunksRef = useRef<Blob[]>([])
  const pttStartTimeRef = useRef<number>(0)
  const [learnedPrefMsg, setLearnedPrefMsg] = useState<string | null>(null)
  const [voiceHint, setVoiceHint] = useState<string | null>(null)
  // Transient on-screen overlay showing the transcribed PTT command while the edit applies
  const [pttOverlay, setPttOverlay] = useState<string | null>(null)
  const pttOverlayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const learnedPrefTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Patient management (edit, reassign, create)
  const [showPatientPicker, setShowPatientPicker] = useState(false)
  const [pickerMode, setPickerMode] = useState<'menu' | 'edit' | 'reassign' | 'create'>('menu')
  const [pickerPatients, setPickerPatients] = useState<any[]>([])
  const [pickerSearch, setPickerSearch] = useState('')
  const [loadingPicker, setLoadingPicker] = useState(false)
  const [newLastName, setNewLastName] = useState('')
  const [newFirstName, setNewFirstName] = useState('')
  const [newDob, setNewDob] = useState('')
  const [creatingPatient, setCreatingPatient] = useState(false)
  const [editFirstName, setEditFirstName] = useState('')
  const [editLastName, setEditLastName] = useState('')
  const [editDob, setEditDob] = useState('')
  const [savingEdit, setSavingEdit] = useState(false)
  const [scanning, setScanning] = useState(false)
  const scanRef = useRef<HTMLInputElement>(null)

  // Provenance state
  const [provenance, setProvenance] = useState<Record<string, Array<{ note_text: string; transcript_start: number; transcript_end: number }>>>({})
  const [highlightedRange, setHighlightedRange] = useState<{ start: number; end: number } | null>(null)
  const transcriptPanelRef = useRef<HTMLDivElement>(null)
  const highlightTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Streaming state
  const [isRefining, setIsRefining] = useState(false)
  const [refineComplete, setRefineComplete] = useState(false)
  const [doctorEditedSections, setDoctorEditedSections] = useState<Set<string>>(new Set())
  const [criticScore, setCriticScore] = useState<number | null>(null)
  const [criticFlags, setCriticFlags] = useState<CriticFlag[]>([])
  const [showFlagDetails, setShowFlagDetails] = useState(false)

  // Codes & Instructions
  const [codes, setCodes] = useState<any>(null)
  const [codingAnalysis, setCodingAnalysis] = useState<any>(null)
  const [instructions, setInstructions] = useState<any>(null)
  const [loadingCodes, setLoadingCodes] = useState(false)
  const [codesProLocked, setCodesProLocked] = useState(false)
  const [loadingInstructions, setLoadingInstructions] = useState(false)

  const streamAbortRef = useRef<{ abort: () => void } | null>(null)
  const autoGenerated = useRef(false)

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      streamAbortRef.current?.abort()
      if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current)
      if (pttRecorderRef.current && pttRecorderRef.current.state !== 'inactive') {
        pttRecorderRef.current.stream?.getTracks().forEach((t) => t.stop())
        pttRecorderRef.current.stop()
      }
    }
  }, [])

  useEffect(() => {
    if (id) loadEncounterAndNotes()
  }, [id, noteId])

  // Load codes when switching to codes tab (needs note.id)
  useEffect(() => {
    if (activeTab === 'codes' && note?.id && !codingAnalysis && !loadingCodes) {
      loadCodes()
    }
  }, [activeTab, note?.id])

  // Load instructions when switching to instructions tab
  useEffect(() => {
    if (activeTab === 'instructions' && id && !instructions && !loadingInstructions) {
      loadInstructions()
    }
  }, [activeTab])

  async function loadEncounterAndNotes() {
    try {
      const enc = await api.getEncounter(id!)
      setEncounter(enc)

      const notes = await api.listNotes(id!)
      // "Record more" returns here with ?regen=1 after appending audio: force a
      // full regenerate so the note reflects the new dictation.
      const forceRegen = searchParams.get('regen') === '1' && !autoGenerated.current
      if (forceRegen && enc.transcript) {
        autoGenerated.current = true
        setLoading(false)
        await handleGenerateNote()
      } else if (notes.length > 0) {
        const target = noteId ? notes.find((n) => n.id === noteId) : notes[0]
        if (target) {
          setNote(target)
          setOriginalSections({ ...target.sections })
          setEditedSections({ ...target.sections })
          setCriticScore(target.critic_score)
          setCriticFlags(target.critic_flags || [])
        }
        setLoading(false)
      } else if (enc.transcript && !autoGenerated.current) {
        autoGenerated.current = true
        setLoading(false)
        await handleGenerateNote()
      } else {
        setLoading(false)
      }
    } catch (err: any) {
      setError(err.message)
      setLoading(false)
    }
  }

  async function loadCodes() {
    if (!note?.id) return
    setLoadingCodes(true)
    try {
      // Try the full analysis endpoint first, fall back to basic codes
      try {
        const analysis = await api.getCodingAnalysis(note.id)
        setCodingAnalysis(analysis)
      } catch {
        // Fall back to basic codes
        const data = await api.getCodes(note.id)
        setCodes(data)
      }
    } catch (err: any) {
      if (err?.message && String(err.message).toLowerCase().includes('pro plan')) { setCodesProLocked(true); setLoadingCodes(false); return }
      console.warn('Failed to load codes:', err)
      setCodes({ icd10_codes: [], cpt_codes: [], em_level: null })
    } finally {
      setLoadingCodes(false)
    }
  }

  async function loadInstructions() {
    setLoadingInstructions(true)
    try {
      const data = await api.getPatientInstructions(id!)
      setInstructions(data)
    } catch (err) {
      console.warn('Failed to load instructions:', err)
      setInstructions({ instructions: '' })
    } finally {
      setLoadingInstructions(false)
    }
  }

  // Provenance click handler
  function handleProvenanceClick(transcriptStart: number, transcriptEnd: number) {
    if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current)
    setHighlightedRange({ start: transcriptStart, end: transcriptEnd })
    setActiveTab('context')
    setTimeout(() => {
      const el = document.getElementById('provenance-highlight')
      if (el && transcriptPanelRef.current) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }, 100)
    highlightTimeoutRef.current = setTimeout(() => setHighlightedRange(null), 3000)
  }

  const handleStreamEvent = useCallback((event: any) => {
    switch (event.type) {
      case 'draft': {
        setGenerating(false)
        setIsRefining(true)
        const draftNote: Note = {
          id: event.note_id,
          encounter_id: id!,
          sections: event.sections,
          full_text: event.full_text || '',
          status: 'refining',
          critic_score: null,
          critic_flags: [],
          missing_sections: [],
          suggested_icd10: {},
          suggested_cpt: {},
          suggested_em_level: null,
          version: 1,
          created_at: new Date().toISOString(),
        }
        setNote(draftNote)
        setEditedSections({ ...event.sections })
        setOriginalSections({ ...event.sections })
        setDoctorEditedSections(new Set())
        break
      }
      case 'critic': {
        setCriticScore(event.critic_score)
        setCriticFlags(event.critic_flags || [])
        break
      }
      case 'provenance': {
        setProvenance(event.provenance || {})
        break
      }
      case 'revision': {
        const changed = new Set<string>(event.changed_sections || [])
        setEditedSections((prev) => {
          const next = { ...prev }
          for (const [key, val] of Object.entries(event.sections as Record<string, string>)) {
            if (!doctorEditedSections.has(key)) {
              next[key] = val
            }
          }
          return next
        })
        setOriginalSections((prev) => ({ ...prev, ...event.sections }))
        const toHighlight = new Set<string>()
        changed.forEach((s) => { if (!doctorEditedSections.has(s)) toHighlight.add(s) })
        setUpdatedSections(toHighlight)
        setTimeout(() => setUpdatedSections(new Set()), 2000)
        break
      }
      case 'complete': {
        setIsRefining(false)
        setRefineComplete(true)
        setTimeout(() => setRefineComplete(false), 2000)
        // Update encounter status to draft
        try { api.updateEncounterStatus(id!, 'draft') } catch {}
        setNote((prev) => prev ? {
          ...prev,
          id: event.note_id || prev.id,
          sections: event.sections,
          full_text: event.full_text || '',
          status: 'draft',
          critic_score: event.critic_score,
          critic_flags: event.critic_flags || [],
          missing_sections: event.missing_sections || [],
          version: event.version || 1,
        } : prev)
        setEditedSections((prev) => {
          const next = { ...prev }
          for (const [key, val] of Object.entries(event.sections as Record<string, string>)) {
            if (!doctorEditedSections.has(key)) next[key] = val
          }
          return next
        })
        setCriticScore(event.critic_score)
        setCriticFlags(event.critic_flags || [])
        break
      }
      case 'error': {
        setError(event.message || 'Stream error')
        setIsRefining(false)
        setGenerating(false)
        break
      }
    }
  }, [id, doctorEditedSections])

  function handlePatientNameClick() {
    if (!showPatientPicker) {
      openPatientPicker()
    } else {
      setShowPatientPicker(false)
    }
  }

  async function openPatientPicker() {
    setShowPatientPicker(true)
    setPickerMode('menu')
    setLoadingPicker(true)
    try {
      const data = await api.listPatients(undefined, 200)
      setPickerPatients(data.patients || [])
      // Load current patient DOB for edit mode
      if (encounter?.patient_id) {
        const p = data.patients?.find((p: any) => p.id === encounter.patient_id)
        if (p) {
          setEditFirstName(p.first_name || '')
          setEditLastName(p.last_name || '')
          setEditDob(p.dob ? p.dob.split('T')[0] : '')
        }
      }
    } catch (err) {
      console.error(err)
    } finally {
      setLoadingPicker(false)
    }
  }

  async function savePatientEdit() {
    if (!encounter?.patient_id || !editLastName.trim() || !editFirstName.trim()) return
    setSavingEdit(true)
    try {
      await api.updatePatient(encounter.patient_id, {
        first_name: editFirstName.trim(),
        last_name: editLastName.trim(),
        dob: editDob || null,
      })
      setEncounter({ ...encounter, patient_name: `${editLastName.trim()}, ${editFirstName.trim()}` })
      setShowPatientPicker(false)
    } catch (err) {
      console.error('Failed to update patient:', err)
    } finally {
      setSavingEdit(false)
    }
  }

  async function assignPatientToEncounter(patientId: string, displayName?: string) {
    if (!encounter || !id) return
    try {
      await api.updateEncounter(id, { patient_id: patientId })
      const name = displayName || (() => {
        const p = pickerPatients.find(p => p.id === patientId)
        return p ? `${p.last_name}, ${p.first_name}` : 'Patient'
      })()
      setEncounter({ ...encounter, patient_id: patientId, patient_name: name })
      setShowPatientPicker(false)
      setPickerMode('menu')
    } catch (err) {
      console.error('Failed to assign patient:', err)
    }
  }

  async function createAndAssignPatient() {
    if (!newLastName.trim() || !newFirstName.trim()) return
    setCreatingPatient(true)
    try {
      const created = await api.createPatient({
        first_name: newFirstName.trim(),
        last_name: newLastName.trim(),
        dob: newDob || undefined,
      })
      await assignPatientToEncounter(created.id, `${created.last_name}, ${created.first_name}`)
      setNewLastName(''); setNewFirstName(''); setNewDob('')
    } catch (err: any) {
      alert(err.message || 'Failed to create patient')
    } finally {
      setCreatingPatient(false)
    }
  }

  async function handleScanPhoto(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    setScanning(true)
    try {
      const reader = new FileReader()
      const base64 = await new Promise<string>((resolve, reject) => {
        reader.onload = () => resolve(reader.result as string)
        reader.onerror = reject
        reader.readAsDataURL(file)
      })
      const result = await api.scanPatientImage(base64)
      if (result.last_name) setNewLastName(result.last_name)
      if (result.first_name) setNewFirstName(result.first_name)
      if (result.dob) setNewDob(result.dob)
      setPickerMode('create')
    } catch {
      alert('Could not read patient info from image.')
    } finally {
      setScanning(false)
      if (scanRef.current) scanRef.current.value = ''
    }
  }

  // Visit/note types the doctor can switch to from the note screen. Mirrors the
  // EncounterPage selector and the backend EncounterType enum.
  const NOTE_TYPE_OPTIONS: { value: string; label: string }[] = [
    { value: 'office', label: 'Office Visit' },
    { value: 'consult', label: 'Consult' },
    { value: 'op_note', label: 'Op Note' },
    { value: 'procedure', label: 'Procedure' },
    { value: 'inpatient', label: 'Rounding' },
    { value: 'telehealth', label: 'Telehealth' },
  ]

  // Change the note type without regenerating. Persisting the type must not
  // discard the doctor's manual edits, so we never auto-regenerate here.
  async function handleChangeNoteType(newType: string) {
    if (generating) return
    if (!newType || newType === (encounter?.encounter_type || '')) return
    // Persist the new type ONLY. Switching type must never auto-regenerate the
    // note — that would silently discard the doctor's manual edits. Regeneration
    // stays an explicit action (the Regenerate button), which picks up this type.
    try {
      await api.updateEncounter(id!, { encounter_type: newType })
      setEncounter((prev) => (prev ? { ...prev, encounter_type: newType } : prev))
    } catch (err) {
      console.warn('Failed to change note type:', err)
    }
  }

  async function handleGenerateNote() {
    setGenerating(true)
    setError(null)
    setIsRefining(false)
    setRefineComplete(false)
    setCriticScore(null)
    setCriticFlags([])
    try { api.updateEncounterStatus(id!, 'processing') } catch {}

    const stream = api.generateNoteStream(id!, handleStreamEvent, encounter?.patient_id || undefined)
    streamAbortRef.current = stream
  }

  function handleSectionEdit(key: string, value: string) {
    setEditedSections((prev) => ({ ...prev, [key]: value }))
    setDoctorEditedSections((prev) => new Set(prev).add(key))
  }

  function applyRevisedNote(revised: any) {
    const changed = new Set<string>()
    const diffs: Record<string, DiffPart[]> = {}
    // Use the live ref (not the closure `note`) so async callers like the PTT
    // recorder.onstop diff against what's actually on screen.
    const baseline = noteRef.current || note
    if (baseline) {
      Object.keys(revised.sections).forEach((key: string) => {
        if (revised.sections[key] !== baseline.sections[key]) {
          changed.add(key)
          diffs[key] = diffWords(baseline.sections[key] || '', revised.sections[key] || '')
        }
      })
    }
    setUpdatedSections(changed)
    setRevisionDiffs(diffs)
    setNote(revised)
    setOriginalSections({ ...revised.sections })
    setEditedSections({ ...revised.sections })
    setCriticScore(revised.critic_score)
    setCriticFlags(revised.critic_flags || [])
    setTimeout(() => setUpdatedSections(new Set()), 3000)
    if (revised.learned_preference) {
      if (learnedPrefTimerRef.current) clearTimeout(learnedPrefTimerRef.current)
      setLearnedPrefMsg(revised.learned_preference)
      learnedPrefTimerRef.current = setTimeout(() => setLearnedPrefMsg(null), 8000)
    }
  }

  // Listen for note revisions dispatched by ChatWidget
  useEffect(() => {
    const h = (e: any) => applyRevisedNote(e.detail)
    window.addEventListener('scribey:note-revised', h)
    return () => window.removeEventListener('scribey:note-revised', h)
  })

  async function handleRevise() {
    if (!note || !revisionMessage.trim()) return
    setIsRevising(true)
    setError(null)
    try {
      const revised = await api.reviseNote(note.id, revisionMessage)
      applyRevisedNote(revised)
      setRevisionMessage('')
    } catch (err: any) {
      setError(err.message || 'Failed to revise note')
    } finally {
      setIsRevising(false)
    }
  }

  async function handlePttStart() {
    if (!note || isPttRecording || isPttProcessing) return
    setError(null)
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      pttChunksRef.current = []
      pttStartTimeRef.current = Date.now()
      let recOpts: MediaRecorderOptions = {}
      if (MediaRecorder.isTypeSupported('audio/webm')) {
        recOpts = { mimeType: 'audio/webm' }
      }
      const recorder = new MediaRecorder(stream, recOpts)
      recorder.ondataavailable = (e: BlobEvent) => {
        if (e.data.size > 0) pttChunksRef.current.push(e.data)
      }
      pttRecorderRef.current = recorder
      recorder.start()
      setIsPttRecording(true)
    } catch (err: any) {
      setError(micErrorMessage(err))
    }
  }

  async function handlePttEnd() {
    if (!isPttRecording || !pttRecorderRef.current || !note) return
    const recorder = pttRecorderRef.current
    const duration = Date.now() - pttStartTimeRef.current
    setIsPttRecording(false)

    // Stop all tracks
    recorder.stream?.getTracks().forEach((t: MediaStreamTrack) => t.stop())

    return new Promise<void>((resolve) => {
      recorder.onstop = async () => {
        if (duration < 300) {
          // Too short, discard
          resolve()
          return
        }
        const mimeType = recorder.mimeType || 'audio/webm'
        const rawBlob = new Blob(pttChunksRef.current, { type: mimeType })
        pttChunksRef.current = []
        setIsPttProcessing(true)
        setVoiceHint(null)
        try {
          const noteId = (noteRef.current || note)!.id
          // Decode to 16 kHz mono PCM client-side so transcription uses the configured
          // provider (AWS) — keeps PTT audio off Deepgram until we have their BAA.
          const blob = await blobToPcm16kMono(rawBlob)
          // STEP 1 — fast transcribe-only (~1s): show the heard command overlay IMMEDIATELY,
          // before the slow LLM revision runs.
          const { transcript } = await api.voiceTranscribe(noteId, blob)
          if (transcript) {
            if (pttOverlayTimerRef.current) clearTimeout(pttOverlayTimerRef.current)
            setPttOverlay(transcript)
            pttOverlayTimerRef.current = setTimeout(() => setPttOverlay(null), 3000)
            setVoiceHint(transcript)
            setTimeout(() => setVoiceHint(null), 6000)
          }
          // STEP 2 — apply the edit via the (proven) typed revise path.
          const revised = await api.reviseNote(noteId, transcript)
          applyRevisedNote(revised)
        } catch (err: any) {
          setError(err.message || 'Voice revision failed')
        } finally {
          setIsPttProcessing(false)
          resolve()
        }
      }
      recorder.stop()
    })
  }

  async function handleApprove() {
    if (!note) return
    try {
      const diffs = Object.keys(editedSections)
        .filter((s) => editedSections[s] !== originalSections[s])
        .map((s) => ({ section: s, before_text: originalSections[s], after_text: editedSections[s] }))
      if (diffs.length > 0) await api.saveEditDiffs(note.id, diffs)
      const approved = await api.approveNote(note.id)
      setNote(approved)
      // Update encounter status
      try { await api.updateEncounterStatus(id!, 'final') } catch {}
      setTimeout(() => navigate('/'), 1500)
    } catch (err: any) {
      setError(err.message || 'Failed to approve note')
    }
  }

  async function handleUnapprove() {
    if (!note) return
    try {
      const reverted = await api.unapproveNote(note.id)
      setNote(reverted)
    } catch (err: any) {
      setError(err.message || 'Failed to unapprove note')
    }
  }

  function buildFullText(): string {
    return Object.entries(editedSections)
      .map(([key, val]) => `${getSectionLabel(key)}:\n${val}`)
      .join('\n\n')
  }

  function copyToClipboard(text: string, label?: string) {
    navigator.clipboard.writeText(text)
    setCopiedSection(label || 'full')
    setTimeout(() => setCopiedSection(null), 2000)
  }

  function handlePrint() {
    const printContent = buildFullText()
    const printWindow = window.open('', '', 'width=800,height=600')
    if (!printWindow) return
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Dr. Scribey Note - ${encounter?.patient_name || 'Patient'}</title>
        <style>
          body {
            font-family: 'Times New Roman', serif;
            font-size: 12pt;
            line-height: 1.6;
            margin: 1in;
            color: #000;
          }
          h1 {
            font-size: 16pt;
            margin-bottom: 0.5em;
          }
          .header {
            margin-bottom: 1em;
            font-size: 10pt;
            color: #666;
          }
          pre {
            white-space: pre-wrap;
            font-family: 'Times New Roman', serif;
            margin: 0;
          }
          @media print {
            body { margin: 0.5in; }
          }
        </style>
      </head>
      <body>
        <h1>${encounter?.patient_name || 'Unknown Patient'}</h1>
        <div class="header">
          <div>${encounter?.encounter_date || ''} • ${encounter?.encounter_type || ''}</div>
          <div>Dr. Scribey v${note?.version || 1}</div>
        </div>
        <pre>${printContent}</pre>
      </body>
      </html>
    `)
    printWindow.document.close()
    printWindow.focus()
    setTimeout(() => {
      printWindow.print()
      printWindow.close()
    }, 250)
  }

  function handleDownloadTxt() {
    const content = buildFullText()
    const header = `${encounter?.patient_name || 'Unknown Patient'}\n${encounter?.encounter_date || ''} • ${encounter?.encounter_type || ''}\nDr. Scribey v${note?.version || 1}\n\n${'-'.repeat(60)}\n\n`
    const fullContent = header + content
    
    const blob = new Blob([fullContent], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${encounter?.patient_name?.replace(/[^a-z0-9]/gi, '_') || 'note'}_${encounter?.encounter_date || 'unknown'}.txt`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  function toggleSection(key: string) {
    setCollapsedSections((prev) => {
      const next = new Set(prev)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })
  }

  function getSectionLabel(key: string): string {
    const labels: Record<string, string> = {
      preop_diagnosis: 'PREOPERATIVE DIAGNOSIS', postop_diagnosis: 'POSTOPERATIVE DIAGNOSIS',
      procedure_performed: 'PROCEDURE PERFORMED', surgeon: 'SURGEON', assistant: 'ASSISTANT',
      anesthesia: 'ANESTHESIA', anesthesiologist: 'ANESTHESIOLOGIST',
      indication: 'INDICATION', findings: 'FINDINGS', implants: 'IMPLANTS',
      technique: 'TECHNIQUE', ebl: 'ESTIMATED BLOOD LOSS', complications: 'COMPLICATIONS',
      specimens: 'SPECIMENS', drains: 'DRAINS', disposition: 'DISPOSITION',
      hpi: 'HISTORY OF PRESENT ILLNESS', ros: 'REVIEW OF SYSTEMS',
      exam: 'PHYSICAL EXAM', physical_exam: 'PHYSICAL EXAM', imaging: 'DIAGNOSTIC STUDIES',
      assessment: 'ASSESSMENT', plan: 'PLAN',
    }
    return labels[key] || key.toUpperCase().replace(/_/g, ' ')
  }

  // @ts-expect-error TS6133 — kept for upcoming critic UI
  function getCriticColor() {
    if (criticScore === null) return 'bg-gray-500'
    if (criticScore > 0.8) return 'bg-green-500'
    if (criticScore > 0.6) return 'bg-yellow-500'
    return 'bg-red-500'
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-pulse text-brand-500 text-lg">Loading...</div>
      </div>
    )
  }

  if (generating && !note) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <div className="animate-spin h-10 w-10 border-4 border-brand-500 border-t-transparent rounded-full" />
        <p className="text-gray-500 dark:text-gray-400">Generating note...</p>
        <p className="text-gray-500 dark:text-gray-600 text-sm">Draft will appear momentarily</p>
      </div>
    )
  }

  if (!note && encounter && !encounter.transcript) {
    return (
      <div className="max-w-2xl mx-auto mt-8 text-center">
        <FileText size={48} className="mx-auto text-gray-500 dark:text-gray-600 mb-4" />
        <p className="text-gray-500 dark:text-gray-400">No transcript available. Record audio first.</p>
        <button onClick={() => navigate(`/encounter/${id}`)} className="mt-4 text-brand-400 underline">
          Go to encounter
        </button>
      </div>
    )
  }

  if (!note) {
    return (
      <div className="max-w-2xl mx-auto mt-8 text-center">
        <p className="text-red-600 dark:text-red-400 mb-4">{error || 'Failed to generate note'}</p>
        <button onClick={handleGenerateNote} className="bg-brand-600 text-white px-6 py-2 rounded-lg">
          Try Again
        </button>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full -mt-4 -mx-4">
      {/* Transient PTT command overlay — shows the heard command, then fades */}
      {pttOverlay && (
        <div className="fixed inset-0 z-[60] pointer-events-none flex items-start justify-center pt-[18vh]">
          <div
            key={pttOverlay}
            className="animate-ptt-overlay max-w-md mx-4 px-5 py-3 rounded-2xl bg-gray-900/85 dark:bg-black/80 backdrop-blur-sm border border-white/10 shadow-2xl text-center"
          >
            <div className="flex items-center justify-center gap-2 text-[10px] uppercase tracking-wider text-brand-300 mb-1">
              <Mic size={11} />
              <span>Heard</span>
            </div>
            <div className="text-sm text-white/95 font-medium leading-snug">&ldquo;{pttOverlay}&rdquo;</div>
          </div>
        </div>
      )}
      {/* Confetti burst */}
      {showConfetti && (
        <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
          {Array.from({ length: 20 }).map((_, i) => (
            <div
              key={i}
              className="absolute animate-confetti text-2xl"
              style={{
                left: `${5 + Math.random() * 90}%`,
                animationDelay: `${Math.random() * 0.8}s`,
                animationDuration: `${1.5 + Math.random() * 1.5}s`,
              }}
            >
              {CONFETTI_PIECES[i % CONFETTI_PIECES.length]}
            </div>
          ))}
        </div>
      )}

      {/* Header */}
      <div className="border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 px-4 py-2 flex-shrink-0">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300">
            <ArrowLeft size={18} />
            <span className="hidden sm:inline text-sm">Back</span>
          </button>

          <div className="text-center relative">
            <h1
              className="text-base font-bold text-gray-900 dark:text-gray-100 cursor-pointer hover:text-brand-600 dark:hover:text-brand-400 transition-colors inline-flex items-center gap-1"
              onClick={handlePatientNameClick}
              title="Edit patient"
            >
              {encounter?.patient_name || <span className="text-gray-400 italic">No patient — tap to assign</span>}
              <ChevronDown size={12} className="text-gray-400" />
            </h1>
            <div className="text-xs text-gray-400 dark:text-gray-500">
              {encounter?.encounter_date} • v{note.version}
            </div>

            {/* Patient management dropdown */}
            {showPatientPicker && (
              <div className="absolute top-full mt-1 left-1/2 -translate-x-1/2 w-72 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 p-2">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                    {pickerMode === 'menu' ? 'Patient' : pickerMode === 'edit' ? 'Edit Patient' : pickerMode === 'reassign' ? 'Reassign Patient' : 'New Patient'}
                  </span>
                  <button onClick={() => { setShowPatientPicker(false); setPickerMode('menu') }} className="text-gray-400 hover:text-gray-600 text-xs">✕</button>
                </div>

                {/* Menu mode */}
                {pickerMode === 'menu' && (
                  <div className="space-y-0.5">
                    {encounter?.patient_id && (
                      <button onClick={() => setPickerMode('edit')}
                        className="w-full text-left px-3 py-2 text-xs text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors">
                        ✏️ Edit name / DOB
                      </button>
                    )}
                    <button onClick={() => { setPickerMode('reassign'); setPickerSearch('') }}
                      className="w-full text-left px-3 py-2 text-xs text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors">
                      🔄 {encounter?.patient_id ? 'Reassign to different patient' : 'Assign patient'}
                    </button>
                    <button onClick={() => { setPickerMode('create'); setNewLastName(''); setNewFirstName(''); setNewDob('') }}
                      className="w-full text-left px-3 py-2 text-xs text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors">
                      ➕ Create new patient
                    </button>
                    <button onClick={() => scanRef.current?.click()} disabled={scanning}
                      className="w-full text-left px-3 py-2 text-xs text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors disabled:opacity-50">
                      📷 {scanning ? 'Scanning...' : 'Scan patient sticker'}
                    </button>
                    <input ref={scanRef} type="file" accept="image/*" capture="environment" onChange={handleScanPhoto} className="hidden" />
                    {encounter?.patient_id && (
                      <button onClick={() => navigate(`/patients/${encounter.patient_id}`)}
                        className="w-full text-left px-3 py-2 text-xs text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors">
                        👤 View full patient record
                      </button>
                    )}
                  </div>
                )}

                {/* Edit mode */}
                {pickerMode === 'edit' && (
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-1.5">
                      <div>
                        <label className="text-[10px] text-gray-500 dark:text-gray-400">Last</label>
                        <input type="text" value={editLastName} onChange={e => setEditLastName(e.target.value)}
                          className="w-full px-2 py-1.5 text-xs bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded text-gray-900 dark:text-gray-100" autoFocus />
                      </div>
                      <div>
                        <label className="text-[10px] text-gray-500 dark:text-gray-400">First</label>
                        <input type="text" value={editFirstName} onChange={e => setEditFirstName(e.target.value)}
                          className="w-full px-2 py-1.5 text-xs bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded text-gray-900 dark:text-gray-100" />
                      </div>
                    </div>
                    <div>
                      <label className="text-[10px] text-gray-500 dark:text-gray-400">DOB</label>
                      <div className="flex gap-1 items-center">
                        <input type="date" value={editDob} onChange={e => setEditDob(e.target.value)}
                          className="flex-1 px-2 py-1.5 text-xs bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded text-gray-900 dark:text-gray-100" />
                        {editDob && (
                          <button onClick={() => setEditDob('')}
                            className="text-gray-400 hover:text-red-500 dark:hover:text-red-400 p-0.5 shrink-0" title="Clear DOB">
                            <X size={12} />
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-1.5">
                      <button onClick={savePatientEdit} disabled={savingEdit || !editLastName.trim() || !editFirstName.trim()}
                        className="flex-1 px-2 py-1.5 text-xs bg-brand-600 hover:bg-brand-500 text-white rounded font-medium disabled:opacity-50">
                        {savingEdit ? 'Saving...' : 'Save'}
                      </button>
                      <button onClick={() => setPickerMode('menu')}
                        className="px-2 py-1.5 text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">Back</button>
                    </div>
                  </div>
                )}

                {/* Reassign mode */}
                {pickerMode === 'reassign' && (
                  <div>
                    <div className="relative mb-1.5">
                      <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                      <input type="text" value={pickerSearch} onChange={e => setPickerSearch(e.target.value)}
                        placeholder="Search patients..." autoFocus
                        className="w-full pl-7 pr-2 py-1.5 text-xs bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded text-gray-900 dark:text-gray-100" />
                    </div>
                    <div className="max-h-40 overflow-y-auto">
                      {loadingPicker ? (
                        <p className="text-xs text-gray-500 p-2 text-center">Loading...</p>
                      ) : pickerPatients.filter(p => {
                        if (!pickerSearch) return true
                        const q = pickerSearch.toLowerCase()
                        return `${p.last_name || ''} ${p.first_name || ''} ${p.name || ''}`.toLowerCase().includes(q)
                      }).map(p => (
                        <button key={p.id} onClick={() => assignPatientToEncounter(p.id)}
                          className={`w-full text-left px-2 py-1.5 text-xs rounded transition-colors ${
                            p.id === encounter?.patient_id
                              ? 'bg-brand-50 dark:bg-brand-950 text-brand-700 dark:text-brand-300 font-medium'
                              : 'text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700'
                          }`}>
                          {p.last_name}, {p.first_name}{p.dob ? ` · ${new Date(p.dob).toLocaleDateString()}` : ''}
                        </button>
                      ))}
                    </div>
                    <button onClick={() => setPickerMode('menu')}
                      className="w-full mt-1 px-2 py-1 text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 text-left">← Back</button>
                  </div>
                )}

                {/* Create mode */}
                {pickerMode === 'create' && (
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-1.5">
                      <div>
                        <label className="text-[10px] text-gray-500 dark:text-gray-400">Last</label>
                        <input type="text" value={newLastName} onChange={e => setNewLastName(e.target.value)}
                          className="w-full px-2 py-1.5 text-xs bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded text-gray-900 dark:text-gray-100" autoFocus />
                      </div>
                      <div>
                        <label className="text-[10px] text-gray-500 dark:text-gray-400">First</label>
                        <input type="text" value={newFirstName} onChange={e => setNewFirstName(e.target.value)}
                          className="w-full px-2 py-1.5 text-xs bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded text-gray-900 dark:text-gray-100" />
                      </div>
                    </div>
                    <div>
                      <label className="text-[10px] text-gray-500 dark:text-gray-400">DOB</label>
                      <input type="date" value={newDob} onChange={e => setNewDob(e.target.value)}
                        className="w-full px-2 py-1.5 text-xs bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded text-gray-900 dark:text-gray-100" />
                    </div>
                    <div className="flex gap-1.5">
                      <button onClick={createAndAssignPatient} disabled={creatingPatient || !newLastName.trim() || !newFirstName.trim()}
                        className="flex-1 px-2 py-1.5 text-xs bg-brand-600 hover:bg-brand-500 text-white rounded font-medium disabled:opacity-50">
                        {creatingPatient ? '...' : 'Add & Assign'}
                      </button>
                      <button onClick={() => setPickerMode('menu')}
                        className="px-2 py-1.5 text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">Back</button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            {!(note.status === 'approved' || note.status === 'signed') ? (
              <>
                <button 
                  onClick={() => { setNote(null); setGenerating(true); handleGenerateNote() }}
                  className="flex items-center gap-1 bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 hover:bg-gray-200 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300 px-3 py-1.5 rounded-lg text-sm font-medium"
                >
                  Regenerate
                </button>
                <button onClick={handleApprove} className="flex items-center gap-1 bg-green-600 hover:bg-green-500 text-white px-3 py-1.5 rounded-lg text-sm font-medium">
                  <Check size={14} /> Approve
                </button>
              </>
            ) : (
              <div className="flex items-center gap-2">
                <span className="flex items-center gap-1 text-sm font-medium text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950 px-3 py-1.5 rounded-lg">
                  <Check size={14} /> Approved
                </span>
                <button
                  onClick={handleUnapprove}
                  className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 px-2 py-1.5"
                  title="Revert to draft for editing"
                >
                  Unapprove
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 flex-shrink-0">
        <div className="max-w-7xl mx-auto flex">
          {(['context', 'note', 'codes', 'instructions', 'letters'] as TabType[]).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-3 text-sm font-medium transition-colors capitalize ${
                activeTab === tab
                  ? 'text-brand-400 border-b-2 border-brand-500'
                  : 'text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {/* Refining indicator */}
      {isRefining && (
        <div className="bg-brand-500/20 px-4 py-1.5 flex items-center gap-2 flex-shrink-0">
          <div className="animate-pulse h-2 w-2 rounded-full bg-brand-500" />
          <span className="text-xs text-brand-400">Refining note...</span>
        </div>
      )}

      {/* Complete indicator */}
      {refineComplete && (
        <div className="bg-green-500/20 px-4 py-1.5 flex items-center gap-2 flex-shrink-0">
          <span className="text-xs text-green-400">✓ Note ready</span>
        </div>
      )}

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto p-4">
          {/* CONTEXT TAB */}
          {activeTab === 'context' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Consent attestation (only present when verbally detected in the dictation) */}
              {(encounter as any)?.consent_attestation && (
                <div className="lg:col-span-2 flex items-start gap-2 bg-emerald-50 dark:bg-emerald-900/15 border border-emerald-200 dark:border-emerald-800 rounded-lg px-3 py-2">
                  <span className="text-emerald-500 mt-0.5 flex-shrink-0">✓</span>
                  <p className="text-xs text-emerald-800 dark:text-emerald-300 leading-relaxed">{(encounter as any).consent_attestation}</p>
                </div>
              )}
              {/* Transcript */}
              <div>
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3 uppercase tracking-wide">Transcript</h3>
                <div className="mb-3">
                  <div className="relative">
                    <Search size={14} className="absolute left-3 top-2.5 text-gray-400 dark:text-gray-500" />
                    <input
                      type="text"
                      value={transcriptSearch}
                      onChange={(e) => setTranscriptSearch(e.target.value)}
                      placeholder="Search transcript..."
                      className="w-full bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-gray-200 pl-8 pr-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                    />
                  </div>
                </div>
                <div className="bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4 max-h-[70vh] overflow-y-auto" ref={transcriptPanelRef}>
                  <div className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">
                    {highlightedRange && encounter?.transcript ? (() => {
                      const t = encounter.transcript
                      const { start, end } = highlightedRange
                      const s = Math.max(0, Math.min(start, t.length))
                      const e = Math.max(s, Math.min(end, t.length))
                      return (
                        <>
                          {t.slice(0, s)}
                          <mark id="provenance-highlight" className="bg-brand-500/20 rounded px-0.5">{t.slice(s, e)}</mark>
                          {t.slice(e)}
                        </>
                      )
                    })() : (encounter?.transcript || 'No transcript available')}
                  </div>
                </div>
              </div>

              {/* Patient Context */}
              <div>
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3 uppercase tracking-wide">Patient Context</h3>
                <div className="bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4 max-h-[70vh] overflow-y-auto space-y-4">
                  {encounter?.patient_id ? (
                    <PatientContextPanel patientId={encounter.patient_id} currentEncounterId={id!} />
                  ) : (
                    <p className="text-sm text-gray-400 dark:text-gray-500">No patient linked to this encounter. Patient context will appear here when a patient is assigned.</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* NOTE TAB */}
          {activeTab === 'note' && (
            <div className="flex gap-4">
              {/* Desktop: transcript sidebar */}
              <div className="hidden xl:block w-80 shrink-0">
                <h3 className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-2">Transcript</h3>
                <div className="bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-3 max-h-[75vh] overflow-y-auto sticky top-4" ref={transcriptPanelRef}>
                  <div className="text-xs text-gray-600 dark:text-gray-400 whitespace-pre-wrap leading-relaxed">
                    {highlightedRange && encounter?.transcript ? (() => {
                      const t = encounter.transcript
                      const { start, end } = highlightedRange
                      const s = Math.max(0, Math.min(start, t.length))
                      const e = Math.max(s, Math.min(end, t.length))
                      return (
                        <>
                          {t.slice(0, s)}
                          <mark id="provenance-highlight" className="bg-brand-500/20 rounded px-0.5">{t.slice(s, e)}</mark>
                          {t.slice(e)}
                        </>
                      )
                    })() : (encounter?.transcript || 'No transcript')}
                  </div>
                </div>
              </div>

              {/* Note content */}
              <div className="flex-1 min-w-0">
              {/* Missing sections alert */}
              {note?.missing_sections && note.missing_sections.length > 0 && !(note.status === 'approved' || note.status === 'signed') && (
                <div className="mb-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl px-4 py-3">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-amber-100 dark:bg-amber-900/40 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-amber-600 text-xs font-bold">!</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-amber-900 dark:text-amber-200 mb-1">Missing sections detected</p>
                      <p className="text-xs text-amber-700 dark:text-amber-400 mb-2">
                        The following sections weren't found in your recording. You can add them by dictating additional details.
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {note.missing_sections.map((ms) => (
                          <span
                            key={ms.section}
                            className={`inline-flex items-center text-xs font-medium px-2.5 py-1 rounded-full ${
                              ms.severity === 'warning'
                                ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300'
                                : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                            }`}
                          >
                            {ms.label}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {/* View toggle + Copy all */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="flex bg-gray-50 dark:bg-gray-800 rounded-lg p-0.5">
                    <button
                      onClick={() => setViewMode('sections')}
                      className={`px-3 py-1 rounded text-xs font-medium ${viewMode === 'sections' ? 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-200' : 'text-gray-400 dark:text-gray-500'}`}
                    >
                      Sections
                    </button>
                    <button
                      onClick={() => setViewMode('full')}
                      className={`px-3 py-1 rounded text-xs font-medium ${viewMode === 'full' ? 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-200' : 'text-gray-400 dark:text-gray-500'}`}
                    >
                      Full
                    </button>
                  </div>
                  {/* Note type — switch template (office/consult/op note/...) and regenerate. */}
                  {!(note.status === 'approved' || note.status === 'signed') && (
                    <select
                      value={encounter?.encounter_type || 'office'}
                      onChange={(e) => handleChangeNoteType(e.target.value)}
                      disabled={generating}
                      title="Change note type (keeps your edits; use Regenerate to rebuild)"
                      className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 text-xs rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:opacity-50 cursor-pointer"
                    >
                      {NOTE_TYPE_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => copyToClipboard(buildFullText())}
                    className="flex items-center gap-1 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 px-3 py-1.5 rounded-lg text-xs"
                    title="Copy full note"
                  >
                    <Copy size={14} />
                    {copiedSection === 'full' ? 'Copied!' : 'Copy'}
                  </button>
                  <button
                    onClick={handlePrint}
                    className="flex items-center gap-1 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 px-3 py-1.5 rounded-lg text-xs"
                    title="Print / Save as PDF"
                  >
                    <Printer size={14} />
                    Print
                  </button>
                  <button
                    onClick={handleDownloadTxt}
                    className="flex items-center gap-1 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 px-3 py-1.5 rounded-lg text-xs"
                    title="Download as .txt"
                  >
                    <Download size={14} />
                    .txt
                  </button>
                </div>
              </div>

              {/* AI quality check — reviewed-state framing (no raw score). Suppressed once signed/approved: a persistent "items to review before signing" on a finalized note is a liability (contemporaneous evidence of unaddressed flags). */}
              {criticScore !== null && !(note.status === 'approved' || note.status === 'signed') && (() => {
                const realFlags = criticFlags.filter((flag: any) => {
                  const f = typeof flag === 'string' ? { severity: 'medium' } : flag
                  return f.severity === 'high' || f.severity === 'medium' || f.severity === 'warning'
                })
                const passed = realFlags.length === 0
                return (
                <div className="mb-4">
                  <button
                    onClick={() => criticFlags.length > 0 && setShowFlagDetails((v) => !v)}
                    className={`w-full flex items-center gap-2.5 rounded-lg px-3 py-2 text-left border ${
                      passed
                        ? 'bg-emerald-50 dark:bg-emerald-500/5 border-emerald-200 dark:border-emerald-500/20'
                        : 'bg-amber-50 dark:bg-amber-500/5 border-amber-200 dark:border-amber-500/30'
                    } ${criticFlags.length > 0 ? 'cursor-pointer' : 'cursor-default'}`}
                  >
                    {passed ? (
                      <>
                        <span className="text-emerald-500 text-sm">✓</span>
                        <span className="text-xs font-medium text-emerald-700 dark:text-emerald-400">
                          AI quality check passed
                        </span>
                        <span className="text-xs text-gray-400 dark:text-gray-500">
                          Note verified against your dictation
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="text-amber-500 text-sm">●</span>
                        <span className="text-xs font-medium text-amber-700 dark:text-amber-400">
                          {realFlags.length} {realFlags.length === 1 ? 'item' : 'items'} to review before signing
                        </span>
                      </>
                    )}
                    {criticFlags.length > 0 && (
                      <span className="ml-auto text-xs text-gray-400 dark:text-gray-500">
                        {showFlagDetails ? 'hide' : 'details'} {showFlagDetails ? '▲' : '▼'}
                      </span>
                    )}
                  </button>
              {showFlagDetails && criticFlags.length > 0 && (
                    <div className="mt-2 space-y-2">
                      {criticFlags.map((flag, i) => {
                        const f = typeof flag === 'string' ? { issue: flag } : flag
                        const sev = f.severity || 'info'
                        const sevStyle =
                          sev === 'high'
                            ? 'border-red-500/40 bg-red-500/5'
                            : sev === 'medium' || sev === 'warning'
                              ? 'border-yellow-500/40 bg-yellow-500/5'
                              : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50'
                        return (
                          <div key={i} className={`border rounded-lg px-3 py-2.5 ${sevStyle}`}>
                            <div className="flex items-center gap-2 mb-1">
                              {f.severity && (
                                <span
                                  className={`text-[10px] font-semibold uppercase tracking-wide ${
                                    sev === 'high' ? 'text-red-500' : sev === 'medium' || sev === 'warning' ? 'text-yellow-500' : 'text-gray-400'
                                  }`}
                                >
                                  {sev}
                                </span>
                              )}
                              {f.section && (
                                <span className="text-[10px] text-gray-400 dark:text-gray-500 uppercase tracking-wide">
                                  {String(f.section).replace(/_/g, ' ')}
                                </span>
                              )}
                              {f.type && (
                                <span className="text-[10px] text-gray-400 dark:text-gray-500">({f.type})</span>
                              )}
                            </div>
                            <p className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed">{f.issue || f.detail || ''}</p>
                            {f.suggestion && (
                              <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed mt-1">
                                💡 {f.suggestion}
                              </p>
                            )}
                          </div>
                        )
                      })}
                      <p className="text-[10px] text-gray-400 dark:text-gray-500 px-1">
                        These are automatic checks comparing the note against your dictation — review the flagged sections before approving.
                      </p>
                    </div>
                  )}
                </div>
                )
              })()}

              {/* Full note view */}
              {viewMode === 'full' && (
                <div className="bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4">
                  {Object.keys(revisionDiffs).length > 0 ? (
                    /* Diff-aware Full view: same deep-red additions / struck deletions as
                       Sections view, but rendered as one continuous note. Click anywhere to
                       dismiss highlights and resume normal reading/editing. */
                    <div
                      onClick={() => setRevisionDiffs({})}
                      className="text-sm text-gray-900 dark:text-gray-200 whitespace-pre-wrap font-sans leading-relaxed cursor-text"
                      title="Click to dismiss highlights"
                    >
                      {Object.entries(editedSections).map(([key, value], si) => (
                        <span key={key}>
                          {si > 0 ? '\n\n' : ''}
                          <span className="font-semibold">{getSectionLabel(key)}:</span>{'\n'}
                          {revisionDiffs[key]
                            ? revisionDiffs[key].map((part, pi) =>
                                part.type === 'same' ? (
                                  <span key={pi}>{part.text}</span>
                                ) : part.type === 'added' ? (
                                  <mark key={pi} className="bg-transparent text-red-700 dark:text-red-300 font-semibold">
                                    {part.text}
                                  </mark>
                                ) : (
                                  <del key={pi} className="text-gray-400 dark:text-gray-600 opacity-70">
                                    {part.text}
                                  </del>
                                )
                              )
                            : <span>{value}</span>}
                        </span>
                      ))}
                    </div>
                  ) : (
                    /* Editable Full view: one continuous note, each section inline-editable
                       via an auto-growing textarea (no tiny scroll window on large screens).
                       Reuses the per-section model so edits flow through handleSectionEdit. */
                    <div className="space-y-3">
                      {Object.entries(editedSections).map(([key, value]) => (
                        <div key={key}>
                          <span className="font-semibold text-sm text-gray-900 dark:text-gray-200">{getSectionLabel(key)}:</span>
                          <AutoGrowTextarea
                            value={value}
                            onChange={(v) => handleSectionEdit(key, v)}
                            className="w-full bg-transparent text-sm text-gray-900 dark:text-gray-200 leading-relaxed border-none focus:outline-none mt-0.5"
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Section view */}
              {viewMode === 'sections' && (
                <div className="space-y-3">
                  {Object.entries(editedSections).map(([key, value]) => {
                    const isCollapsed = collapsedSections.has(key)
                    return (
                      <div
                        key={key}
                        className={`bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-3 transition-all duration-500 ${updatedSections.has(key) ? 'ring-2 ring-brand-500/50' : ''}`}
                      >
                        {/* Section header with controls */}
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <button onClick={() => toggleSection(key)} className="text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
                              {isCollapsed ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
                            </button>
                            <h3 className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wider font-medium">
                              {getSectionLabel(key)}
                            </h3>
                            {doctorEditedSections.has(key) && (
                              <span className="text-[10px] bg-brand-500/20 text-brand-400 px-1.5 py-0.5 rounded">
                                edited
                              </span>
                            )}
                            {revisionDiffs[key] && (
                              <span className="text-[10px] bg-red-700/15 text-red-700 dark:bg-red-500/20 dark:text-red-300 px-1.5 py-0.5 rounded">
                                updated — click text to edit
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            <button onClick={() => copyToClipboard(value, key)} className="p-1 text-gray-500 dark:text-gray-600 hover:text-gray-700 dark:hover:text-gray-400" title="Copy">
                              <Copy size={12} />
                            </button>
                            <button className="p-1 text-gray-500 dark:text-gray-600 hover:text-green-500" title="Good">
                              <ThumbsUp size={12} />
                            </button>
                            <button className="p-1 text-gray-500 dark:text-gray-600 hover:text-red-500" title="Needs work">
                              <ThumbsDown size={12} />
                            </button>
                          </div>
                        </div>

                        {/* Section content */}
                        {!isCollapsed && (
                          <>
                            {revisionDiffs[key] ? (
                              /* Quick-edit diff view: additions in deep red, deletions struck through.
                                 Click anywhere in the text to dismiss and resume normal editing. */
                              <div
                                onClick={() => setRevisionDiffs((prev) => {
                                  const next = { ...prev }
                                  delete next[key]
                                  return next
                                })}
                                className="w-full text-sm text-gray-900 dark:text-gray-200 leading-relaxed whitespace-pre-wrap cursor-text"
                                title="Click to dismiss highlights and edit"
                              >
                                {revisionDiffs[key].map((part, pi) =>
                                  part.type === 'same' ? (
                                    <span key={pi}>{part.text}</span>
                                  ) : part.type === 'added' ? (
                                    <mark key={pi} className="bg-transparent text-red-700 dark:text-red-300 font-semibold">
                                      {part.text}
                                    </mark>
                                  ) : (
                                    <del key={pi} className="text-gray-400 dark:text-gray-600 opacity-70">
                                      {part.text}
                                    </del>
                                  )
                                )}
                              </div>
                            ) : (
                              <AutoGrowTextarea
                                value={value}
                                onChange={(v) => handleSectionEdit(key, v)}
                                className="w-full bg-transparent text-sm text-gray-900 dark:text-gray-200 leading-relaxed border-none focus:outline-none"
                              />
                            )}
                            {/* Provenance links */}
                            {provenance[key] && provenance[key].length > 0 && (
                              <div className="mt-2 flex flex-wrap gap-1">
                                {provenance[key].map((p, i) => (
                                  <button
                                    key={i}
                                    onClick={() => handleProvenanceClick(p.transcript_start, p.transcript_end)}
                                    className="inline-flex items-center gap-0.5 text-[10px] text-gray-400 dark:text-gray-500 hover:text-brand-400 border-b border-dotted border-gray-500 dark:border-gray-600 hover:border-brand-400 transition-colors"
                                    title={p.note_text}
                                  >
                                    <Link size={8} />
                                    <span className="max-w-[120px] truncate">{p.note_text}</span>
                                  </button>
                                ))}
                              </div>
                            )}
                            {copiedSection === key && <div className="text-xs text-green-500 mt-1">Copied!</div>}
                          </>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}

              {/* Learned preference banner (Feature A) */}
              {learnedPrefMsg && (
                <div className="mt-3 flex items-start gap-2 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg px-3 py-2 text-xs text-green-700 dark:text-green-300">
                  <span className="flex-1">✓ Preference saved — future notes will follow: &quot;{learnedPrefMsg}&quot;</span>
                  <button onClick={() => setLearnedPrefMsg(null)} className="text-green-500 hover:text-green-700 dark:hover:text-green-200 flex-shrink-0">
                    <X size={12} />
                  </button>
                </div>
              )}

              {/* Voice transcript hint (Feature B) */}
              {voiceHint && (
                <div className="mt-1 text-xs text-gray-400 dark:text-gray-500 italic px-1">
                  heard: &quot;{voiceHint}&quot;
                </div>
              )}

              {/* Revision input */}
              {!(note.status === 'approved' || note.status === 'signed') && (
                <div className="mt-6 bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-3">
                  <div className="flex gap-2">
                    {/* Hold-to-talk quick edit (PTT) */}
                    <button
                      onPointerDown={handlePttStart}
                      onPointerUp={handlePttEnd}
                      onPointerLeave={handlePttEnd}
                      onPointerCancel={handlePttEnd}
                      onContextMenu={(e) => e.preventDefault()}
                      disabled={isPttProcessing || isRevising}
                      className={`flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all select-none touch-none ${
                        isPttRecording
                          ? 'bg-red-600 text-white shadow-lg shadow-red-600/30 scale-105'
                          : isPttProcessing
                          ? 'bg-brand-600 text-white opacity-80'
                          : 'bg-brand-500/10 text-brand-600 dark:text-brand-400 border border-brand-500/30 hover:bg-brand-500/20 active:scale-95'
                      } disabled:opacity-50`}
                      title="Hold the button, say your fix, release — the note updates itself"
                    >
                      {isPttProcessing ? (
                        <>
                          <div className="animate-spin h-3.5 w-3.5 border-2 border-white border-t-transparent rounded-full" />
                          <span>Applying…</span>
                        </>
                      ) : isPttRecording ? (
                        <>
                          <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
                          </span>
                          <span>Listening — release to apply</span>
                        </>
                      ) : (
                        <>
                          <Mic size={14} />
                          <span>Hold to fix</span>
                        </>
                      )}
                    </button>
                    <input
                      type="text"
                      value={revisionMessage}
                      onChange={(e) => setRevisionMessage(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleRevise()}
                      placeholder={isPttRecording ? 'Listening… release to apply' : isPttProcessing ? 'Applying edit…' : "Type a fix — or hold the mic and say it"}
                      className="flex-1 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-200 px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                      disabled={isRevising || isPttRecording || isPttProcessing}
                    />
                    <button
                      onClick={handleRevise}
                      disabled={isRevising || !revisionMessage.trim() || isPttRecording || isPttProcessing}
                      className="bg-brand-600 hover:bg-brand-500 text-white px-3 py-2 rounded-lg disabled:opacity-50"
                    >
                      {isRevising ? (
                        <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                      ) : (
                        <Send size={16} />
                      )}
                    </button>
                  </div>
                  {error && <div className="text-xs text-red-600 dark:text-red-400 mt-2">{error}</div>}
                </div>
              )}

              {/* Record more — full-width pill below the fix bar (regenerates the note) */}
              {!(note.status === 'approved' || note.status === 'signed') && (
                <button
                  onClick={() => navigate(`/encounter/${id}?autoRecord=true&more=1`)}
                  className="mt-4 w-full flex items-center justify-center gap-2 bg-brand-600 hover:bg-brand-500 text-white py-2.5 rounded-full text-sm font-medium transition-colors"
                  title="Record more audio for this visit and regenerate the note"
                >
                  <Mic size={16} /> Record more
                </button>
              )}
              </div>
            </div>
          )}

          {/* CODES TAB */}
          {activeTab === 'codes' && (
            <div>
              {codesProLocked ? (
                <div className="max-w-md mx-auto text-center py-16 px-6">
                  <div className="text-3xl mb-3">✦</div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Coding &amp; billing support is a Pro feature</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">CPT, ICD-10, and E/M level suggestions with rationale, upcoding guidance, and prior-auth tips — built for procedure-heavy practices.</p>
                  <a href="/settings" className="inline-block bg-brand-600 hover:bg-brand-500 text-white px-4 py-2 rounded-lg text-sm font-medium">Upgrade to Pro</a>
                </div>
              ) : loadingCodes ? (
                <div className="text-center py-12">
                  <div className="animate-spin h-8 w-8 border-4 border-brand-500 border-t-transparent rounded-full mx-auto mb-3" />
                  <p className="text-gray-400 dark:text-gray-500 text-sm">Analyzing note for billing codes...</p>
                  <p className="text-gray-500 dark:text-gray-600 text-xs mt-1">This may take a few seconds</p>
                </div>
              ) : codingAnalysis ? (
                <div className="space-y-6">
                  {/* Advisory Disclaimer */}
                  <div className="bg-yellow-100 dark:bg-yellow-900/10 border border-yellow-300 dark:border-yellow-800/30 rounded-lg px-3 py-2">
                    <p className="text-[11px] text-yellow-600 dark:text-yellow-500/80">⚠️ All coding suggestions are advisory only. The provider bears sole responsibility for verifying codes, selecting diagnoses, and ensuring billing compliance.</p>
                  </div>

                  {/* E/M Level */}
                  {codingAnalysis.em_level && (
                    <div className="bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wide">E/M Level</h4>
                        <span className="text-lg font-bold font-mono text-brand-400">{codingAnalysis.em_level.code}</span>
                      </div>
                      <div className="text-sm text-gray-700 dark:text-gray-300 mb-3">{codingAnalysis.em_level.description || codingAnalysis.em_level.rationale}</div>
                      
                      {/* MDM Breakdown */}
                      {codingAnalysis.em_level.mdm && (
                        <div className="grid grid-cols-3 gap-2 mb-3">
                          <div className="bg-gray-50 dark:bg-gray-800 rounded p-2 text-center">
                            <div className="text-[10px] text-gray-400 dark:text-gray-500 uppercase">Problems</div>
                            <div className="text-xs text-gray-700 dark:text-gray-300 font-medium capitalize">{codingAnalysis.em_level.mdm.problems || codingAnalysis.em_level.mdm_complexity}</div>
                          </div>
                          <div className="bg-gray-50 dark:bg-gray-800 rounded p-2 text-center">
                            <div className="text-[10px] text-gray-400 dark:text-gray-500 uppercase">Data</div>
                            <div className="text-xs text-gray-700 dark:text-gray-300 font-medium capitalize">{codingAnalysis.em_level.mdm.data || '—'}</div>
                          </div>
                          <div className="bg-gray-50 dark:bg-gray-800 rounded p-2 text-center">
                            <div className="text-[10px] text-gray-400 dark:text-gray-500 uppercase">Risk</div>
                            <div className="text-xs text-gray-700 dark:text-gray-300 font-medium capitalize">{codingAnalysis.em_level.mdm.risk || '—'}</div>
                          </div>
                        </div>
                      )}

                      {/* Time-based alternative */}
                      {codingAnalysis.em_level.time_based && (
                        <div className="text-xs text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-gray-800 rounded px-2 py-1">
                          ⏱ Time-based: {codingAnalysis.em_level.time_based}
                        </div>
                      )}
                    </div>
                  )}

                  {/* ICD-10 Diagnoses */}
                  {codingAnalysis.icd10_codes?.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wide">ICD-10 Diagnoses</h4>
                        <button
                          onClick={() => copyToClipboard(codingAnalysis.icd10_codes.map((c: any) => `${c.code} - ${c.description}`).join('\n'), 'icd10')}
                          className="text-xs text-brand-400 hover:text-brand-300"
                        >
                          {copiedSection === 'icd10' ? 'Copied!' : 'Copy all'}
                        </button>
                      </div>
                      <div className="space-y-2">
                        {codingAnalysis.icd10_codes.map((code: any, i: number) => (
                          <div key={i} className="bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-3">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="font-mono text-brand-500 text-sm">{code.code}</span>
                                  {i === 0 && <span className="text-[10px] bg-brand-500/20 text-brand-400 px-1.5 py-0.5 rounded">Primary</span>}
                                </div>
                                <div className="text-sm text-gray-700 dark:text-gray-300">{code.description}</div>
                                {code.rationale && <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">{code.rationale}</div>}
                              </div>
                              <div className="flex items-center gap-2 ml-3">
                                {code.confidence && <span className="text-xs text-gray-500 dark:text-gray-600">{(code.confidence * 100).toFixed(0)}%</span>}
                                <button onClick={() => copyToClipboard(code.code)} className="text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
                                  <Copy size={12} />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* CPT Codes */}
                  {codingAnalysis.cpt_codes?.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wide">CPT Procedures</h4>
                        <button
                          onClick={() => copyToClipboard(codingAnalysis.cpt_codes.map((c: any) => `${c.code} - ${c.description}`).join('\n'), 'cpt')}
                          className="text-xs text-brand-400 hover:text-brand-300"
                        >
                          {copiedSection === 'cpt' ? 'Copied!' : 'Copy all'}
                        </button>
                      </div>
                      <div className="space-y-2">
                        {codingAnalysis.cpt_codes.map((code: any, i: number) => (
                          <div key={i} className="bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-3">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <span className="font-mono text-brand-500 text-sm">{code.code}</span>
                                <div className="text-sm text-gray-700 dark:text-gray-300">{code.description}</div>
                                {code.rationale && <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">{code.rationale}</div>}
                              </div>
                              <div className="flex items-center gap-2 ml-3">
                                {code.confidence && <span className="text-xs text-gray-500 dark:text-gray-600">{(code.confidence * 100).toFixed(0)}%</span>}
                                <button onClick={() => copyToClipboard(code.code)} className="text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
                                  <Copy size={12} />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Upcoding Guidance */}
                  {codingAnalysis.upcoding_suggestions && codingAnalysis.upcoding_suggestions.length > 0 && (
                    <div className="bg-blue-100 dark:bg-blue-900/10 border border-blue-300 dark:border-blue-800/30 rounded-lg p-4">
                      <h4 className="text-xs font-medium text-blue-600 dark:text-blue-400 uppercase tracking-wide mb-3">💡 To Code Higher</h4>
                      <ul className="space-y-2">
                        {codingAnalysis.upcoding_suggestions.map((suggestion: any, i: number) => (
                          <li key={i} className="text-sm text-gray-700 dark:text-gray-300 flex gap-2">
                            <span className="text-blue-600 dark:text-blue-400 mt-0.5">→</span>
                            <span>{typeof suggestion === 'string' ? suggestion : suggestion.suggestion || suggestion.text}</span>
                          </li>
                        ))}
                      </ul>
                      {codingAnalysis.next_level && (
                        <div className="mt-3 text-xs text-gray-400 dark:text-gray-500">
                          Target: <span className="text-blue-600 dark:text-blue-400 font-mono">{codingAnalysis.next_level}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Prior Authorization Guidance */}
                  {codingAnalysis.prior_auth_guidance && codingAnalysis.prior_auth_guidance.length > 0 && (
                    <div className="bg-yellow-100 dark:bg-yellow-900/10 border border-yellow-300 dark:border-yellow-800/30 rounded-lg p-4">
                      <h4 className="text-xs font-medium text-yellow-600 dark:text-yellow-400 uppercase tracking-wide mb-3">📋 Prior Auth Guidance</h4>
                      <ul className="space-y-2">
                        {codingAnalysis.prior_auth_guidance.map((item: any, i: number) => (
                          <li key={i} className="text-sm text-gray-700 dark:text-gray-300 flex gap-2">
                            <span className="text-yellow-600 dark:text-yellow-400 mt-0.5">•</span>
                            <span>{typeof item === 'string' ? item : item.guidance || item.text}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Evidence-Based Guidelines */}
                  {codingAnalysis.evidence_guidelines && codingAnalysis.evidence_guidelines.length > 0 && (
                    <div className="bg-green-100 dark:bg-green-900/10 border border-green-300 dark:border-green-800/30 rounded-lg p-4">
                      <h4 className="text-xs font-medium text-green-600 dark:text-green-400 uppercase tracking-wide mb-3">📚 Evidence-Based Guidelines</h4>
                      <ul className="space-y-2">
                        {codingAnalysis.evidence_guidelines.map((item: any, i: number) => (
                          <li key={i} className="text-sm text-gray-700 dark:text-gray-300 flex gap-2">
                            <span className="text-green-600 dark:text-green-400 mt-0.5">✓</span>
                            <span>{typeof item === 'string' ? item : item.guideline || item.text}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ) : codes ? (
                /* Fallback: basic codes display */
                <div className="space-y-6">
                  <div className="bg-yellow-100 dark:bg-yellow-900/10 border border-yellow-300 dark:border-yellow-800/30 rounded-lg px-3 py-2">
                    <p className="text-[11px] text-yellow-600 dark:text-yellow-500/80">⚠️ All coding suggestions are advisory only. The provider bears sole responsibility for verifying codes and ensuring billing compliance.</p>
                  </div>
                  {codes.em_level && (
                    <div className="bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-3">
                      <span className="font-mono text-brand-500">E/M: {codes.em_level.code}</span>
                      <div className="text-sm text-gray-700 dark:text-gray-300">{codes.em_level.rationale}</div>
                    </div>
                  )}
                  {codes.icd10_codes?.length > 0 && (
                    <div>
                      <h4 className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase mb-2">ICD-10 Diagnoses</h4>
                      <div className="space-y-2">
                        {codes.icd10_codes.map((c: any, i: number) => (
                          <div key={i} className="bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-3">
                            <span className="font-mono text-brand-500 text-sm">{c.code}</span>
                            <div className="text-sm text-gray-700 dark:text-gray-300">{c.description}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {codes.cpt_codes?.length > 0 && (
                    <div>
                      <h4 className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase mb-2">CPT Codes</h4>
                      <div className="space-y-2">
                        {codes.cpt_codes.map((c: any, i: number) => (
                          <div key={i} className="bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-3">
                            <span className="font-mono text-brand-500 text-sm">{c.code}</span>
                            <div className="text-sm text-gray-700 dark:text-gray-300">{c.description}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-400 dark:text-gray-500">
                  <p>No codes generated yet. Generate a note first.</p>
                </div>
              )}
            </div>
          )}

          {/* INSTRUCTIONS TAB */}
          {activeTab === 'letters' && (
            <div className="max-w-4xl mx-auto p-6">
              {note ? (
                <LetterGenerator noteId={note.id} />
              ) : (
                <p className="text-sm text-gray-400 dark:text-gray-500">Generate a note first — letters are written from the signed note content.</p>
              )}
            </div>
          )}

          {activeTab === 'instructions' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Patient Instructions</h3>
                <div className="flex gap-2">
                  <button className="flex items-center gap-1 text-sm bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 px-3 py-1.5 rounded-lg">
                    <Copy size={14} />
                    Copy all
                  </button>
                  <button className="flex items-center gap-1 text-sm bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 px-3 py-1.5 rounded-lg">
                    <Printer size={14} />
                    Print
                  </button>
                </div>
              </div>

              {loadingInstructions ? (
                <div className="text-center py-8">
                  <div className="animate-spin h-6 w-6 border-4 border-brand-500 border-t-transparent rounded-full mx-auto" />
                </div>
              ) : (
                <div className="bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4">
                  <div className="text-sm text-gray-900 dark:text-gray-200 whitespace-pre-wrap leading-relaxed">
                    {instructions?.instructions || 'No patient instructions generated yet.'}
                  </div>
                </div>
              )}
            </div>
          )}

        </div>
      </div>
    </div>
  )
}
